import {useNavigate} from 'react-router-dom';
import {LogoutOutlined, MenuOutlined, SettingOutlined, SwapOutlined} from '@ant-design/icons';
import type {MenuProps} from 'antd';
import {Avatar, Button, Dropdown, Layout, Tag} from 'antd';
import clsx from 'clsx';

import {useAuth} from '@hooks/useAuth';
import {useBreakpoint} from '@hooks/useBreakpoint';

import {displayName, firstInitial, ROLE_LABEL} from './AppHeader.utils';

import styles from './AppHeader.module.scss';

type AppHeaderProps = {
  title: string;
  subtitle?: string;
  onOpenDrawer: () => void;
  onOpenPicker: () => void;
};

export const AppHeader = ({title, subtitle, onOpenDrawer, onOpenPicker}: AppHeaderProps) => {
  const navigate = useNavigate();
  const {user, permission, logout} = useAuth();
  const {isMobile, isTablet} = useBreakpoint();

  const role = permission?.role;

  const handleLogout = () => {
    logout();
    navigate('/login', {replace: true});
  };

  const userMenu: MenuProps['items'] = [
    {
      key: 'header',
      type: 'group',
      label: (
        <div className={styles.menuHeader}>
          <div className={styles.menuHeaderRow}>
            <Avatar size={40} className={styles.menuHeaderAvatar}>
              {firstInitial(user)}
            </Avatar>
            <div className={styles.menuHeaderBody}>
              <div className={styles.menuHeaderName}>{displayName(user)}</div>
              <div className={styles.menuHeaderEmail}>{user?.email ?? '—'}</div>
            </div>
          </div>
          <div className={styles.menuChips}>
            {role && <Tag className={styles.chipRole}>{ROLE_LABEL[role] ?? role}</Tag>}
            {permission?.organization?.title && (
              <Tag className={styles.chipContext}>{permission.organization.title}</Tag>
            )}
          </div>
        </div>
      ),
    },
    {type: 'divider'},
    {
      key: 'switch',
      icon: <SwapOutlined />,
      label: 'Сменить доступ',
      onClick: onOpenPicker,
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Настройки',
      onClick: () => navigate('/settings'),
    },
    {type: 'divider'},
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Выйти',
      danger: true,
      onClick: handleLogout,
    },
  ];

  return (
    <Layout.Header className={clsx(styles.header, isMobile && styles.headerMobile)}>
      <div className={styles.titleBlock}>
        {isTablet && (
          <Button
            type="text"
            size="large"
            icon={<MenuOutlined />}
            onClick={onOpenDrawer}
            aria-label="Открыть меню"
            className={styles.drawerButton}
          />
        )}
        <div className={styles.titleWrapper}>
          <h2 className={clsx(styles.title, isMobile && styles.titleMobile)}>{title}</h2>
          {subtitle && !isMobile && <div className={styles.subtitle}>{subtitle}</div>}
        </div>
      </div>

      <div className={styles.actions}>
        <Dropdown
          menu={{items: userMenu, className: styles.menu}}
          placement="bottomRight"
          trigger={['click']}
          arrow={{pointAtCenter: true}}
        >
          <button type="button" className={styles.userTrigger}>
            <Avatar size={isMobile ? 34 : 30} className={styles.userAvatar}>
              {firstInitial(user)}
            </Avatar>
            {!isMobile && (
              <div className={styles.userInfo}>
                <div className={styles.userName}>{displayName(user)}</div>
                {role && <div className={styles.userRole}>{ROLE_LABEL[role] ?? role}</div>}
              </div>
            )}
          </button>
        </Dropdown>
      </div>
    </Layout.Header>
  );
};
